import React, { forwardRef } from 'react';
/**
 * Renders an input component.
 *
 * @param {Object} props - The component props.
 * @param {string} [props.size='m'] - The size of the input group. Possible values are 'sm', 'sm_2', 'm', 'l', 'xl'.
 * @param {string} [props.position='right'] - The position of the icon within the input group. Possible values are 'left', 'right', 'center'.
 * @param {boolean} [props.isError=false] - Indicates whether the input group has an error.
 * @param {string} props.svg - The SVG image source for the icon.
 * @returns {JSX.Element} The rendered input group component.
 */
const InputGroup = forwardRef(
  (
    {
      setIsValueVisible,
      propertyId,
      size = 'm',
      position = 'right',
      isError = false,
      svg,
      className,
      ...props
    },
    ref
  ) => {
  // This is sample payload of how input group should be.
  // const size = "m";
  // const position = "right";
  // const svg = (
  // 	<svg
  // 		width="17"
  // 		height="18"
  // 		viewBox="0 0 17 18"
  // 		fill="none"
  // 		xmlns="http://www.w3.org/2000/svg"
  // 	>
  // 		<path
  // 			d="M12.9728 15.5824C13.1103 16.1173 13.6554 16.4394 14.1903 16.3018C14.7252 16.1643 15.0473 15.6192 14.9098 15.0843L12.9728 15.5824ZM2.09025 15.0843C1.95271 15.6192 2.27482 16.1643 2.80971 16.3018C3.3446 16.4394 3.8897 16.1173 4.02724 15.5824L2.09025 15.0843ZM3.10403 15.7799L3.55802 14.8888L3.10403 15.7799ZM1.72015 14.396L2.61115 13.942L1.72015 14.396ZM15.2799 14.396L14.3888 13.942L15.2799 14.396ZM13.896 15.7799L13.442 14.8888L13.896 15.7799ZM13.896 2.22015L13.442 3.11115L13.896 2.22015ZM15.2799 3.60403L14.3888 4.05802L15.2799 3.60403ZM3.10403 2.22015L3.55802 3.11115L3.10403 2.22015ZM1.72015 3.60403L2.61115 4.05802L1.72015 3.60403ZM10.9635 8.50521C10.9635 9.86579 9.86058 10.9688 8.5 10.9688V12.9688C10.9651 12.9688 12.9635 10.9704 12.9635 8.50521H10.9635ZM8.5 10.9688C7.13942 10.9688 6.03646 9.86579 6.03646 8.50521H4.03646C4.03646 10.9704 6.03485 12.9688 8.5 12.9688V10.9688ZM6.03646 8.50521C6.03646 7.14463 7.13942 6.04167 8.5 6.04167V4.04167C6.03485 4.04167 4.03646 6.04006 4.03646 8.50521H6.03646ZM8.5 6.04167C9.86058 6.04167 10.9635 7.14463 10.9635 8.50521H12.9635C12.9635 6.04006 10.9651 4.04167 8.5 4.04167V6.04167ZM14.9098 15.0843C14.2639 12.5724 11.476 10.9688 8.5 10.9688V12.9688C10.9233 12.9688 12.6329 14.2607 12.9728 15.5824L14.9098 15.0843ZM4.02724 15.5824C4.36708 14.2607 6.0767 12.9688 8.5 12.9688V10.9688C5.52405 10.9688 2.73615 12.5724 2.09025 15.0843L4.02724 15.5824ZM6.44167 2.875H10.5583V0.875H6.44167V2.875ZM14.625 6.94167V11.0583H16.625V6.94167H14.625ZM10.5583 15.125H6.44167V17.125H10.5583V15.125ZM2.375 11.0583V6.94167H0.375V11.0583H2.375ZM6.44167 15.125C5.53842 15.125 4.92112 15.1242 4.44324 15.0852C3.97703 15.0471 3.73225 14.9776 3.55802 14.8888L2.65004 16.6709C3.1532 16.9272 3.69049 17.0303 4.28037 17.0785C4.85859 17.1258 5.57142 17.125 6.44167 17.125V15.125ZM0.375 11.0583C0.375 11.9286 0.374222 12.6414 0.421464 13.2196C0.46966 13.8095 0.572766 14.3468 0.82914 14.85L2.61115 13.942C2.52238 13.7678 2.45291 13.523 2.41482 13.0568C2.37578 12.5789 2.375 11.9616 2.375 11.0583H0.375ZM3.55802 14.8888C3.15034 14.6811 2.81888 14.3497 2.61115 13.942L0.82914 14.85C1.22861 15.634 1.86603 16.2714 2.65004 16.6709L3.55802 14.8888ZM14.625 11.0583C14.625 11.9616 14.6242 12.5789 14.5852 13.0568C14.5471 13.523 14.4776 13.7678 14.3888 13.942L16.1709 14.85C16.4272 14.3468 16.5303 13.8095 16.5785 13.2196C16.6258 12.6414 16.625 11.9286 16.625 11.0583H14.625ZM10.5583 17.125C11.4286 17.125 12.1414 17.1258 12.7196 17.0785C13.3095 17.0303 13.8468 16.9272 14.35 16.6709L13.442 14.8888C13.2678 14.9776 13.023 15.0471 12.5568 15.0852C12.0789 15.1242 11.4616 15.125 10.5583 15.125V17.125ZM14.3888 13.942C14.1811 14.3497 13.8497 14.6811 13.442 14.8888L14.35 16.6709C15.134 16.2714 15.7714 15.634 16.1709 14.85L14.3888 13.942ZM10.5583 2.875C11.4616 2.875 12.0789 2.87578 12.5568 2.91482C13.023 2.95291 13.2678 3.02238 13.442 3.11115L14.35 1.32914C13.8468 1.07277 13.3095 0.96966 12.7196 0.921464C12.1414 0.874222 11.4286 0.875 10.5583 0.875V2.875ZM16.625 6.94167C16.625 6.07142 16.6258 5.35859 16.5785 4.78037C16.5303 4.19049 16.4272 3.6532 16.1709 3.15004L14.3888 4.05802C14.4776 4.23225 14.5471 4.47703 14.5852 4.94324C14.6242 5.42112 14.625 6.03842 14.625 6.94167H16.625ZM13.442 3.11115C13.8497 3.31888 14.1811 3.65034 14.3888 4.05802L16.1709 3.15004C15.7714 2.36603 15.134 1.72861 14.35 1.32914L13.442 3.11115ZM6.44167 0.875C5.57142 0.875 4.85859 0.874222 4.28037 0.921464C3.69049 0.96966 3.1532 1.07277 2.65004 1.32914L3.55802 3.11115C3.73225 3.02238 3.97703 2.95291 4.44324 2.91482C4.92112 2.87578 5.53842 2.875 6.44167 2.875V0.875ZM2.375 6.94167C2.375 6.03842 2.37578 5.42112 2.41482 4.94324C2.45291 4.47703 2.52238 4.23225 2.61115 4.05802L0.82914 3.15004C0.572766 3.6532 0.46966 4.19049 0.421464 4.78037C0.374222 5.35859 0.375 6.07142 0.375 6.94167H2.375ZM2.65004 1.32914C1.86603 1.72861 1.22861 2.36603 0.82914 3.15004L2.61115 4.05802C2.81888 3.65034 3.15034 3.31888 3.55802 3.11115L2.65004 1.32914Z"
  // 			fill="#667085"
  // 		/>
  // 	</svg>
  // );

  if (!svg) {
    position = '';
  }

  const sizeClasses = {
    xsm: '!w-[7rem] text-sm px-2 py-1 rounded-lg border-2 placeholder:p-xs-regular',
    sm: 'text-sm px-2 py-1 rounded-lg border-2 placeholder:p-xs-regular',
    sm_2: 'font-medium-2 px-2 py-2 rounded-[5px] border-2 placeholder:font-medium-2',
    m: 'px-2 p-xs-regular py-2.5 rounded-lg border-2 placeholder:p-xs-regular',
    mw: 'px-2 p-xs-regular py-2.5 rounded-lg border-2 placeholder:p-xs-regular !w-[320px]',
    l: 'text-lg px-4 py-3 rounded-lg border-2 placeholder:p-xs-regular',
    xl: 'text-xl px-5 py-4 rounded-lg border-2 placeholder:p-xs-regular',
  };
  const iconPositionClasses = {
    left: 'left-3',
    right: 'right-3',
    center: 'left-3',
  };

  const colorClassName = isError ? 'text-error' : 'text-black';
  const borderClassName = isError ? 'border-error' : 'border-primary';
  const borderOnFocus = isError ? 'border-error' : 'border-secondary';

  const paddingPosition = {
    left: 'pl-10',
    right: 'pr-10',
    center: 'px-9',
  };

  const resolvedReadOnly =
    props.readOnly ??
    (props.value !== undefined && typeof props.onChange !== 'function');

  const inputProps = {
    ...props,
    readOnly: resolvedReadOnly,
  };

  return (
    <div className="relative">
      {position === 'left' && (
        <div
          className={`absolute top-1/2 transform -translate-y-1/2 ${iconPositionClasses[position]}`}
        >
          <img src={svg} alt="" />
        </div>
      )}
      <input
        type="text"
        className={`${paddingPosition[position]} ${sizeClasses[size]} ${borderClassName}  disabled:bg-frameDisabled w-full  ${colorClassName} font-normal focus:outline-none focus:${borderOnFocus} focus:border-2 ${className}`}
        {...inputProps}
        ref={ref}
      />
      <div
        className="cursor-pointer"
        onClick={() =>
          setIsValueVisible((prev) => {
            if (prev.includes(propertyId)) {
              return prev.filter((item) => item !== propertyId);
            }
            return [...prev, propertyId];
          })
        }
      >
        {position === 'right' && (
          <div
            className={`absolute top-1/2 transform -translate-y-1/2 ${iconPositionClasses[position]}`}
          >
            <img src={svg} alt="" />
          </div>
        )}
        {position === 'center' && (
          <div
            className={`absolute top-1/2 transform -translate-y-1/2 ${iconPositionClasses[position]}`}
          >
            <img src={svg} alt="" />
          </div>
        )}
      </div>
    </div>
  );
  }
);

InputGroup.displayName = 'InputGroup';

export default InputGroup;
